import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Dropdown,
  Form,
  Input,
  Modal,
  Switch,
  Table,
  App as AntApp,
} from "antd";
import { DeleteOutlined, EditOutlined, MoreOutlined, PlusOutlined } from "@ant-design/icons";

import { enterprisesApi } from "../api/enterprises";
import type { Enterprise } from "../api/types";
import { PageHeader } from "../components/PageHeader";
import { Pill } from "../components/Pill";
import { formatDateTimeIST } from "../utils/format";

export function Enterprises() {
  const { notification, modal } = AntApp.useApp();
  const [rows, setRows] = useState<Enterprise[]>([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [editing, setEditing] = useState<Enterprise | null>(null);
  const [addForm] = Form.useForm();
  const [editForm] = Form.useForm();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await enterprisesApi.list({ page_size: 200 });
      setRows(res.results);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (editing) {
      editForm.setFieldsValue({ name: editing.name, is_active: editing.is_active });
    }
  }, [editing, editForm]);

  const onAdd = async () => {
    try {
      const vals = await addForm.validateFields();
      await enterprisesApi.create(vals);
      notification.success({ message: "Enterprise added" });
      setAddOpen(false);
      addForm.resetFields();
      load();
    } catch (e: unknown) {
      const err = e as { response?: { data?: Record<string, string[] | string> } };
      const data = err.response?.data;
      if (data) {
        const msg =
          (Array.isArray(data.name) ? data.name[0] : (data.name as string)) ||
          (typeof data.detail === "string" ? data.detail : null) ||
          "Could not create enterprise";
        notification.error({ message: msg });
      }
    }
  };

  const onEdit = async () => {
    if (!editing) return;
    try {
      const vals = await editForm.validateFields();
      await enterprisesApi.update(editing.id, vals);
      notification.success({ message: "Enterprise updated" });
      setEditing(null);
      load();
    } catch (e: unknown) {
      const err = e as { response?: { data?: Record<string, string[] | string> } };
      const data = err.response?.data;
      if (data) {
        const msg =
          (Array.isArray(data.name) ? data.name[0] : (data.name as string)) ||
          (typeof data.detail === "string" ? data.detail : null) ||
          "Could not update enterprise";
        notification.error({ message: msg });
      }
    }
  };

  const onDelete = (e: Enterprise) => {
    modal.confirm({
      title: `Delete enterprise "${e.name}"?`,
      content:
        "Products tagged with this enterprise will keep their other fields, but their Enterprise selection will be cleared.",
      okText: "Delete",
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await enterprisesApi.remove(e.id);
          notification.success({ message: "Enterprise deleted" });
          load();
        } catch (err: unknown) {
          const ax = err as { response?: { data?: { detail?: string } } };
          notification.error({
            message: "Delete failed",
            description: ax.response?.data?.detail || "Could not delete enterprise.",
          });
        }
      },
    });
  };

  return (
    <div className="page-shell">
      <PageHeader
        title="Enterprises"
        subtitle="Companies that products belong to. Admins pick from this list when adding a product."
        right={
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setAddOpen(true)}>
            Add Enterprise
          </Button>
        }
      />

      <Alert
        type="info"
        showIcon
        message={`${rows.length} ${rows.length === 1 ? "enterprise" : "enterprises"} configured.`}
        style={{ marginBottom: 20 }}
      />

      <Table
        rowKey="id"
        loading={loading}
        dataSource={rows}
        pagination={false}
        columns={[
          { title: "Name", dataIndex: "name", render: (v: string) => <b>{v}</b> },
          {
            title: "Status",
            dataIndex: "is_active",
            render: (v: boolean) => <Pill kind={v ? "active" : "inactive"} />,
          },
          {
            title: "Created",
            dataIndex: "created_at",
            render: (v: string) => <span className="small-muted">{formatDateTimeIST(v)}</span>,
          },
          {
            title: "",
            key: "_actions",
            render: (_: unknown, row: Enterprise) => (
              <Dropdown
                menu={{
                  items: [
                    { key: "edit", label: "Edit", icon: <EditOutlined />, onClick: () => setEditing(row) },
                    { type: "divider" },
                    {
                      key: "delete",
                      label: "Delete",
                      icon: <DeleteOutlined />,
                      danger: true,
                      onClick: () => onDelete(row),
                    },
                  ],
                }}
                trigger={["click"]}
              >
                <Button type="text" icon={<MoreOutlined />} />
              </Dropdown>
            ),
          },
        ]}
      />

      <Modal
        title="Add Enterprise"
        open={addOpen}
        onCancel={() => setAddOpen(false)}
        onOk={onAdd}
        okText="Create"
      >
        <Form layout="vertical" form={addForm} requiredMark={false}>
          <Form.Item
            label="Name"
            name="name"
            rules={[{ required: true, min: 2, max: 255 }]}
          >
            <Input placeholder="e.g. Reflexlabs Pvt Ltd" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={editing ? `Edit ${editing.name}` : "Edit Enterprise"}
        open={!!editing}
        onCancel={() => setEditing(null)}
        onOk={onEdit}
        okText="Save"
        destroyOnClose
      >
        <Form layout="vertical" form={editForm} requiredMark={false} preserve={false}>
          <Form.Item
            label="Name"
            name="name"
            rules={[{ required: true, min: 2, max: 255 }]}
          >
            <Input />
          </Form.Item>
          <Form.Item label="Active" name="is_active" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
